import React, { PropsWithChildren, useMemo, useState } from 'react';
import { dividerClasses, Menu } from '@mui/material';

import { isInsertingComponentDesign as isInsertingComponentDesignSelector } from '../../../../store/selectors/isInsertingComponentDesignSelector';
import { inViewOnlyMode } from '../../../../store/selectors/viewOnlyModeSelectors';
import { clearRightClickedItems } from '../../../../store/slices/viewportSlice';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { SceneMenuContext } from './context';

export function SceneMenuWrapper(props: PropsWithChildren<{ requestedStartingReplacement?: string }>) {
  const dispatch = useAppDispatch();

  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
  const [activeReplacement, setActiveReplacement] = useState<string | null>(props.requestedStartingReplacement ?? null);

  const contextValue = useMemo(
    () => ({
      activeReplacement,
      activateReplacement: (id: string) => {
        setActiveReplacement(id);
      },
      deactivateReplacement: () => {
        setActiveReplacement(null);
      },
      containerEl,
    }),
    [activeReplacement, setActiveReplacement, containerEl],
  );

  const isViewOnlyComponent = useAppSelector(inViewOnlyMode);
  const isInsertingComponentDesign = useAppSelector(isInsertingComponentDesignSelector);
  const rightClickedItem = useAppSelector((state) => state.viewport.rightClickedItem);

  // The component is considered "view only" during insertion, and we need to allow context menus during profile
  // insertion for web joint segments.
  if ((!isInsertingComponentDesign && isViewOnlyComponent) || rightClickedItem == null) return null;

  const anchorPosition = {
    top: rightClickedItem.position.clientPosition.y,
    left: rightClickedItem.position.clientPosition.x,
  };

  return (
    <SceneMenuContext.Provider value={contextValue}>
      <Menu
        open
        sx={{
          [`& .${dividerClasses.root}:first-child`]: { display: 'none' },
          [`& .${dividerClasses.root}:last-child`]: { display: 'none' },
          [`& .${dividerClasses.root} + .${dividerClasses.root}`]: { display: 'none' },
        }}
        anchorReference="anchorPosition"
        anchorPosition={anchorPosition}
        onClose={() => {
          dispatch(clearRightClickedItems());
        }}
      >
        {props.children}
        <div ref={setContainerEl} />
      </Menu>
    </SceneMenuContext.Provider>
  );
}
